import { getTodayStr, isYmd } from '../reports/edit/editCalc';
import { AuditChange, AuditDetails, AuditFilters, AuditResult, AuditRow } from './auditApi';

export const EMPTY_FILTERS: AuditFilters = {
  from: '',
  to: '',
  module: '',
  action: '',
  kind: '',
  result: '',
  user: '',
  pharmacy: '',
  q: ''
};

/** Блоки details, которые экран показывает отдельными разделами (остальное идет в «Дополнительно») */
const KNOWN_DETAILS = ['changes', 'changesTotal', 'snapshot', 'ids', 'total', 'params', 'truncated', 'note'];

const SECRET_MASK = '***';

function shiftDays(ymd: string, delta: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + delta)).toISOString().slice(0, 10);
}

/** Период «последние N дней, включая сегодня» */
export function lastDaysPeriod(days: number): { from: string; to: string } {
  const today = getTodayStr();
  return { from: shiftDays(today, -(days - 1)), to: today };
}

/** Фильтры при открытии экрана: последние 7 дней, чтобы не читать весь журнал */
export function defaultFilters(): AuditFilters {
  return { ...EMPTY_FILTERS, ...lastDaysPeriod(7) };
}

/** Период можно отправлять: даты пустые либо корректные и начало не позже конца */
export function isRangeValid(from: string, to: string): boolean {
  if (from && !isYmd(from)) return false;
  if (to && !isYmd(to)) return false;
  return !(from && to && from > to);
}

/** Фильтры такие же, как при открытии экрана (сбрасывать нечего) */
export function isDefaultFilters(filters: AuditFilters): boolean {
  const initial = defaultFilters();
  return (Object.keys(initial) as Array<keyof AuditFilters>).every((name) => filters[name].trim() === initial[name]);
}

/** 2026-10-08 14:05:09 -> { date: 08.10.2026, time: 14:05:09 } */
export function splitDateTime(value: string): { date: string; time: string } {
  const [ymd, time] = String(value || '').split(' ');
  const [y, m, d] = (ymd || '').split('-');
  if (!y || !m || !d) return { date: value || '', time: '' };
  return { date: `${d}.${m}.${y}`, time: time || '' };
}

export function formatDateTime(value: string): string {
  const { date, time } = splitDateTime(value);
  return time ? `${date} ${time}` : date;
}

/** Значение для экрана: пусто - тире, да/нет - по-русски, структуры - компактным JSON */
export function valueToText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function isMasked(value: unknown): boolean {
  return typeof value === 'string' && value.indexOf(SECRET_MASK) === 0;
}

/** Основная строка пользователя: логин и ФИО, если они отличаются */
export function userLabel(row: Pick<AuditRow, 'username' | 'fullName'>): string {
  if (!row.username) return 'Не определен';
  return row.fullName && row.fullName !== row.username ? `${row.username} · ${row.fullName}` : row.username;
}

/** Аптека записи: номер, а если его нет - логин аптеки */
export function pharmacyLabel(row: Pick<AuditRow, 'pharmacyNum' | 'pharmacyUsername'>): string {
  if (row.pharmacyNum && row.pharmacyUsername && row.pharmacyNum !== row.pharmacyUsername) {
    return `${row.pharmacyNum} (${row.pharmacyUsername})`;
  }
  return row.pharmacyNum || row.pharmacyUsername || '';
}

/** Объект действия: название и идентификатор */
export function entityLabel(row: Pick<AuditRow, 'entityLabel' | 'entityId'>): string {
  if (row.entityLabel && row.entityId && row.entityLabel !== row.entityId) return `${row.entityLabel} · ${row.entityId}`;
  return row.entityLabel || row.entityId || '';
}

export const RESULT_STYLES: Record<AuditResult, { color: string; background: string; border: string }> = {
  success: { color: '#047857', background: '#ecfdf5', border: '#a7f3d0' },
  error: { color: '#b91c1c', background: '#fef2f2', border: '#fecaca' },
  denied: { color: '#b45309', background: '#fffbeb', border: '#fde68a' }
};

export function sectionPath(row: Pick<AuditRow, 'moduleTitle' | 'sectionTitle'>): string {
  return row.sectionTitle ? `${row.moduleTitle} → ${row.sectionTitle}` : row.moduleTitle;
}

/** Список изменений из details (без некорректных элементов) */
export function readChanges(details: AuditDetails | null): AuditChange[] {
  if (!details || !Array.isArray(details.changes)) return [];
  return details.changes.filter((item) => item && typeof item === 'object');
}

/** Пары «название - значение» для объекта (снимок записи, параметры запроса); скаляр отдается одной парой */
export function toPairs(value: unknown): Array<{ key: string; value: string }> {
  if (value === null || value === undefined) return [];
  if (typeof value !== 'object' || Array.isArray(value)) return [{ key: '', value: valueToText(value) }];
  const source = value as Record<string, unknown>;
  return Object.keys(source).map((key) => ({ key, value: valueToText(source[key]) }));
}

/** Дополнительные данные записи, которых нет среди известных блоков */
export function extraPairs(details: AuditDetails | null): Array<{ key: string; value: string }> {
  if (!details) return [];
  const rest: Record<string, unknown> = {};
  Object.keys(details).forEach((key) => {
    if (KNOWN_DETAILS.indexOf(key) < 0) rest[key] = details[key];
  });
  return toPairs(rest);
}

/** Нужно ли показывать, что список затронутых записей обрезан */
export function hiddenCount(shown: number, total: number | undefined): number {
  return total !== undefined && total > shown ? total - shown : 0;
}

/** Имя файла выгрузки с периодом */
export function csvFileName(filters: AuditFilters): string {
  const from = filters.from || 'start';
  const to = filters.to || getTodayStr();
  return `audit-log_${from}_${to}.csv`;
}
